import React, { ChangeEvent, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import gql from 'graphql-tag';
import { createStyles, makeStyles } from '@material-ui/core';
import {
	AlertBox,
	Breadcrumbs,
	BreadcrumbsText,
	Button,
	Divider,
	ExpansionPanel,
	ExpansionPanelDetails,
	ExpansionPanelSummary,
	ExtendedTheme,
	Heading,
	Link,
	Loader, TextArea,
	TextBlock,
	TextField
} from 'neon-design-system';
import { FieldAttributes, Form, Formik, useField } from 'formik';
import {AlertMessage, PromotionalContent, PromotionalContentNullable} from '../../../shared/types';
import { useApolloClient, useMutation, useQuery } from '@apollo/react-hooks';
import { queryPromotionalContentCache } from './PromotionalContentAddEdit';
import PromotionEditor from '../../ui/admin/PromotionEditor';
import * as Yup from 'yup';
import { FormSnackBarType, generateSnackBarFormMessage, useConstrainTextArea } from '../../../utils/form.utils';
import { ManageTemplateInstance } from '../../ui/ManageTemplate';
import { translateErrorMessage } from '../../../shared/error-handling';
import { MessageTypes, useNotification } from '../../providers/NotificationProvider';
import { baseUrls } from '../../../utils/baseUrls.utils';

interface BreadcrumbsData {
	contentUrl: string;
	contentTitle: string;
	pageTitle: string;
}

const useStyles = makeStyles((theme: ExtendedTheme) => createStyles({
	container: {
		display: 'flex',
		width: '100%',
		height: '100%',
		paddingLeft: theme.spacing(2)
	},
	editContainer: {
		width: '100%',
		display: 'flex',
		justifyContent: 'center',
		paddingBottom: theme.spacing(6),
		marginTop: theme.spacing(2)
	},
	dialogCaption: {
		display: 'flex'
	},
	dialogCheckbox: {
		marginTop: theme.spacing(2)
	},
	rightSideBarContainer: {
		backgroundColor: theme.palette.grey[800],
		marginLeft: theme.spacing(2),
		minWidth: 368,
		width: 368,
		display: 'flex',
		flexDirection: 'column'
	},
	sidebarHeader: {
		backgroundColor: theme.themeColors.grey[700]
	},
	sidebarDetails: {
		backgroundColor: theme.palette.grey[800],
		display: 'block',
		'&>div:not(:last-child)': {
			marginBottom: theme.spacing(2)
		}
	},
	sidebarTemplateDetails: {
		backgroundColor: theme.palette.grey[800],
		display: 'block',
		'&>div:not(:last-child)': {
			marginBottom: theme.spacing(1)
		}
	},
	expansionPanel: {
		overflow: 'visible'
	},
	editContent: {
		maxWidth: 1200,
		width: '100%'
	},
	alert: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(2)
	},
	editor: {
		marginTop: theme.spacing(2),
		maxWidth: 700
	},
	form: {
		height: '100%'
	},
	headerMainAction: {
		padding: theme.spacing(2),
		'&>button': {
			width: '100%'
		}
	},
	divider: {
		width: '100%'
	},
	heading: {
		marginTop: theme.spacing(4),
		marginBottom: theme.spacing(1)
	},
	marginBottom2: {
		marginBottom: `${theme.spacing(2)}px !important`
	}
}));

const SidebarPanel: React.FC<{ label: string; templateDetails?: boolean }> = ({ label, children, templateDetails = false }) => {
	const classes = useStyles();
	return (
		<ExpansionPanel className={classes.expansionPanel} defaultExpanded={true}>
			<ExpansionPanelSummary>{label}</ExpansionPanelSummary>
			<ExpansionPanelDetails
				className={!templateDetails ? classes.sidebarDetails : classes.sidebarTemplateDetails}
			>
				{children}
			</ExpansionPanelDetails>
		</ExpansionPanel>
	);
};

const TextInput: React.FC<FieldAttributes<{ label: string; name: string; placeholder: string; id: string }>> = (
	{ label, name, placeholder, id }
) => {
	const [{ value, ...field }, meta] = useField({ name });
	return <TextField
		{...field}
		value={value}
		label={label}
		error={meta.touched && !!meta.error}
		errorText={meta.error}
		placeholder={placeholder}
		id={id}
	/>;
};

const GET_PROMOTION_PAGE = gql`
query findPromotionPageById($id: ID!) {
	document: findPromotionPageById(id: $id) {
		_id
		name
		html
		teamsChannel
		version
		pageSummary
		socialSharingImageUrl
		successTitle
		successMessage
		promotionalContent {
			_id
		}
	}
}`;

const ADD_PROMOTION_PAGE = gql`
	mutation addPromotionPage($item: PromotionPageInputAdd) {
		addPromotionPage(item: $item) {
			_id
		}
	}
`;

const EDIT_PROMOTION_PAGE = gql`
	mutation editPromotionPage($item: PromotionPageInputEdit) {
		editPromotionPage(item: $item) {
			_id
		}
	}
`;


export const PromotionPageAddEdit = (): JSX.Element => {
	const { _id } = useParams();
	const classes = useStyles();
	const { showMessage } = useNotification();
	const { push } = useHistory();
	const apolloClient = useApolloClient();
	const [pageMessage, setPageMessage] = useState<AlertMessage>({ style: 'warning', message: null });
	const [isPressSubmit, setIsPressSubmit] = useState(false);
	const pageSummaryTextArea = useConstrainTextArea(0, 200);
	const successMessageTextArea = useConstrainTextArea(0, 200);

	const { data, loading } = useQuery(
		GET_PROMOTION_PAGE, { skip: !_id, variables: { id: _id, withTemplate: !!_id }, fetchPolicy: 'network-only', onCompleted: ({ document }) => {
				pageSummaryTextArea?.setCurrentChars(document?.pageSummary?.length || 0)
				successMessageTextArea?.setCurrentChars(document?.successMessage?.length || 0)
		}}
	);

	const editItem = data?.document;

	const cachedItem = readContentFromCache();

	useEffect(() => {
		const filteredPromotionalContent = cachedItem && editItem?.promotionalContent ? editItem.promotionalContent.filter(((content: PromotionalContent) => content._id !== cachedItem._id)) : [];
		if (filteredPromotionalContent?.length) {
			setPageMessage({ style: 'warning', message: 'Heads up! This page is currently in use. Any updates will be reflected everywhere.' });
		}
	}, [cachedItem, editItem]);


	const [addPromotionPageAction] = useMutation(ADD_PROMOTION_PAGE);
	const [editPromotionPageAction] = useMutation(EDIT_PROMOTION_PAGE);

	if (loading) return <Loader />;

	const editMode = !!editItem;

	const breadcrumbs = getBreadcrumbsData(cachedItem);

	function readContentFromCache(): PromotionalContentNullable {
		try {
			const readData = apolloClient.readQuery({ query: queryPromotionalContentCache });
			return readData?.savedItem;
		}
		catch (ex) {
			return null;
		}
	}

	function updateContentInCache(name: string) {
		if (!cachedItem) return;

		cachedItem['newPageName'] = name;

		apolloClient.writeQuery({
			query: queryPromotionalContentCache,
			data: {
				savedItem: cachedItem
			}
		});
	}

	function getBreadcrumbsData(cached: PromotionalContentNullable): BreadcrumbsData {
		const resultValue: BreadcrumbsData = {
			contentTitle: '',
			contentUrl: baseUrls.publisher.promotionalContent.list,
			pageTitle: editMode ? 'Edit landing page' : 'New landing page'
		}

		if (cached) {
			if (cached._id === '-') {
				resultValue.contentTitle = 'New promotional content';
				resultValue.contentUrl = baseUrls.publisher.promotionalContent.add;
			} else {
				resultValue.contentTitle = 'Edit promotional content';
				resultValue.contentUrl = `${baseUrls.publisher.promotionalContent.list}/edit/${cached?._id}`;
			}
		}

		return resultValue;
	}

	return (
		<Formik
			initialValues={{
				name: editItem?.name || '',
				html: editItem?.html || '',
				teamsChannel: editItem?.teamsChannel || '',
				pageSummary: editItem?.pageSummary || '',
				socialSharingImageUrl: editItem?.socialSharingImageUrl || '',
				successTitle: editItem?.successTitle || '',
				successMessage: editItem?.successMessage || ''
			}}
			onSubmit={async (values, { setSubmitting }) => {
				try {
					setSubmitting(true);
					const saveFn = editMode ? editPromotionPageAction : addPromotionPageAction;

					if (!editMode){
						updateContentInCache(values.name);
					}

					await saveFn({
						variables: {
							item: {
								_id,
								name: values.name,
								html: values.html,
								teamsChannel: values.teamsChannel,
								version: editItem?.version || undefined,
								pageSummary: values.pageSummary,
								socialSharingImageUrl: values.socialSharingImageUrl,
								successTitle: values.successTitle,
								successMessage: values.successMessage
							}
						}
					});
					showMessage(
						generateSnackBarFormMessage(
							ManageTemplateInstance.PromotionPage,
							values.name,
							editMode ? FormSnackBarType.Updated : FormSnackBarType.Added
						),
						MessageTypes.info
					);
					push(breadcrumbs.contentUrl);
				} catch (error) {
					setSubmitting(false);
					showMessage(translateErrorMessage(error.message), MessageTypes.error);
				}
			}}
			validationSchema={
				Yup.object({
					name: Yup.string().required('This field is required.'),
					html: Yup.string().required('This field is required.'),
					teamsChannel: Yup.string().required('This field is required.'),
					pageSummary: Yup.string().required('This field is required.'),
					socialSharingImageUrl: Yup.string().required('This field is required.'),
					successTitle: Yup.string().required('This field is required.'),
					successMessage: Yup.string().required('This field is required.')

				})
			}
		>
			{({ values, setFieldValue, errors , touched, setTouched}) =>
				<Form translate="yes" className={classes.form}>
					<div className={classes.container}>
						<div className={classes.editContainer}>
							<div className={classes.editContent}>
								{(cachedItem !== undefined && cachedItem !== null)
									?
									<Breadcrumbs>
										<Link to={baseUrls.publisher.promotionalContent.list}>Promotional Content</Link>
										<Link to={breadcrumbs.contentUrl}> {breadcrumbs.contentTitle}</Link>
										<BreadcrumbsText>{breadcrumbs.pageTitle}</BreadcrumbsText>
									</Breadcrumbs>
									:
									<Breadcrumbs>
										<Link to={baseUrls.publisher.promotionalContent.list}>Promotional Content</Link>
										<BreadcrumbsText>{breadcrumbs.pageTitle}</BreadcrumbsText>
									</Breadcrumbs>
								}

								<Heading className={classes.heading} styleName="title1">Promotional landing page</Heading>
								<TextBlock styleName="body">Add content to build a landing page for promotional content.
								Don't forget to add a main button for the user to register their interest.</TextBlock>
								<div className={classes.alert}>
									<AlertBox
										id="article-add-edit-alert"
										visible={!!pageMessage.message}
										children={pageMessage.message}
										styleName={pageMessage.style}
									/>
								</div>
								<div className={classes.editor}>
									<PromotionEditor
										initialHtml={values.html ?? ' '}
										errorMessage={isPressSubmit ? errors.html as string : undefined}
										onChange={(html) => setFieldValue('html', html)}
									/>
								</div>
							</div>
						</div>

						<div className={classes.rightSideBarContainer}>
							<div className={classes.sidebarHeader}>
								<div className={classes.headerMainAction}>
									<Button id="save-page-button" onClick={() => setIsPressSubmit(true)} colorType="primary" size="large" type="submit">
										{!editMode ? 'Save landing page' : 'Update landing page'}
									</Button>
								</div>
							</div>

							<div>
								<SidebarPanel label="Template details" templateDetails>
									<TextInput id="page-name-input" label="Landing page name" name="name" placeholder="" />
									<TextBlock styleName="caption1" className={classes.marginBottom2}>The page name is visible in the browser and in the social sharing preview.</TextBlock>

									<TextInput id="channel-name-input" label="Microsoft Teams Group/Channel" name="teamsChannel" placeholder="Group/Channel" />
									<TextBlock styleName="caption1">Automatically get notifications to your chosen Teams channel when a user clicks on the button on the landing page.</TextBlock>
								</SidebarPanel>
								<SidebarPanel label="Success message" templateDetails>
									<TextInput id="success-title" label="Title" name="successTitle" placeholder="We will be in touch." />
									<TextBlock styleName="caption1" className={classes.marginBottom2}>Tell the user that they successfully registered their interest in the promotion.</TextBlock>
									<TextArea
										id="success-message"
										label="Message"
										name="successMessage"
										placeholder="A member of the desk will be in contact soon."
										fullWidth
										type="text"
										error={touched.successMessage && !!errors.successMessage}
										errorText={errors.successMessage as string}
										progressBarHelperText={successMessageTextArea?.charsMessage}
										autoComplete="off"
										rowsMax={4}
										value={values.successMessage}
										showProgressBar={true}
										progressBarIndicator={successMessageTextArea?.currentChars}
										progressBarLimit={successMessageTextArea?.charsLimit}
										onChange={(e: ChangeEvent<any>): void => {
											const val: string = e?.target?.value;
											if (val?.length > successMessageTextArea?.charsLimit) {
												return;
											}
											successMessageTextArea?.setCurrentChars(val?.length)
											setFieldValue('successMessage', val);
										}}
										onBlur={() => setTouched({...touched, 'successMessage': true})}
									/>
								</SidebarPanel>
								<SidebarPanel label="Social sharing" templateDetails>
									<TextArea
										id="channel-pageSummary-input"
										label="Page summary"
										name="pageSummary"
										placeholder=""
										className={classes.marginBottom2}
										fullWidth
										type="text"
										error={touched.pageSummary && !!errors.pageSummary}
										errorText={errors.pageSummary as string}
										progressBarHelperText={pageSummaryTextArea?.charsMessage}
										autoComplete="off"
										rowsMax={4}
										value={values.pageSummary}
										showProgressBar={true}
										progressBarIndicator={pageSummaryTextArea?.currentChars}
										progressBarLimit={pageSummaryTextArea?.charsLimit}
										onChange={(e: ChangeEvent<any>): void => {
											const val: string = e?.target?.value;
											if (val?.length > pageSummaryTextArea?.charsLimit) {
												return;
											}
											pageSummaryTextArea?.setCurrentChars(val?.length)
											setFieldValue('pageSummary', val);
										}}
										onBlur={() => setTouched({...touched, 'pageSummary': true})}
									/>
									<TextInput id="page-socialSharingImageUrl-input" label="Social sharing image URL" name="socialSharingImageUrl" placeholder="Image file URL" />
									<TextBlock styleName="caption1">This image will be shown when this page is shared on any social media platform. The image will be cropped to a ratio of 2:1.</TextBlock>
								</SidebarPanel>
							</div>
							<Divider className={classes.divider} />
						</div>
					</div>
				</Form>
			}

		</Formik>
	);
}


export default PromotionPageAddEdit;
